import { ReactNode } from 'react'
import { TFunction } from 'i18next'
import { BiSolidQuoteAltRight } from 'react-icons/bi'
import { BsParagraph } from 'react-icons/bs'
import { LuHeading, LuLink, LuPackage } from 'react-icons/lu'
import { PiList } from 'react-icons/pi'
import { TbCategory, TbLibraryPhoto, TbVideo } from 'react-icons/tb'
import { TfiLayoutMediaLeft } from 'react-icons/tfi'
import i18n from '~/app/locale/i18n'
import { stringifyTextBlockContent } from '../lib/block-content'

export type EditorBlockKind =
  | 'paragraph'
  | 'heading'
  | 'list'
  | 'product'
  | 'productCategory'
  | 'postLink'
  | 'quote'
  | 'mediaText'
  | 'image'
  | 'cover'
  | 'gallery'
  | 'video'
  | 'audio'
  | 'columns'
  | 'file'

export interface EditorBlock {
  id: string
  kind: EditorBlockKind
  label: string
  content: string
}

export interface EditorSeoData {
  storeId: string | null
  tagIds: string[]
  slug: string
}

export interface BlogPostEditorProps {
  className?: string
  value?: EditorBlock[]
  seo?: EditorSeoData
  placeholder?: string
  disabled?: boolean
  onChange?: (value: EditorBlock[]) => void
  onSeoChange?: (value: EditorSeoData) => void
}

export interface EditorBlockItem {
  kind: EditorBlockKind
  label: string
  icon: ReactNode
}

export interface EditorBlockSection {
  title: string
  items: EditorBlockItem[]
}

export const getEditorBlockSections = (
  t: TFunction<['common', 'blog']>,
): EditorBlockSection[] => [
  {
    title: t('blog:sectionText'),
    items: [
      {
        kind: 'paragraph',
        label: t('blog:blockParagraph'),
        icon: <BsParagraph size={32} />,
      },
      {
        kind: 'heading',
        label: t('blog:blockHeading'),
        icon: <LuHeading size={32} />,
      },
      {
        kind: 'list',
        label: t('blog:blockList'),
        icon: <PiList size={32} />,
      },
      {
        kind: 'quote',
        label: t('blog:blockQuote'),
        icon: <BiSolidQuoteAltRight size={32} />,
      },
    ],
  },
  {
    title: t('blog:sectionMedia'),
    items: [
      {
        kind: 'mediaText',
        label: t('blog:blockMediaText'),
        icon: <TfiLayoutMediaLeft size={32} />,
      },
      {
        kind: 'image',
        label: t('blog:blockImage'),
        icon: <TbLibraryPhoto size={32} />,
      },
      {
        kind: 'image',
        label: t('blog:blockVideo'),
        icon: <TbVideo size={32} />,
      },
    ],
  },
  {
    title: t('blog:sectionMore'),
    items: [
      {
        kind: 'product',
        label: t('blog:blockProduct'),
        icon: <LuPackage size={32} />,
      },
      {
        kind: 'productCategory',
        label: t('blog:blockProductCategory'),
        icon: <TbCategory size={32} />,
      },
      {
        kind: 'postLink',
        label: t('blog:blockPostLink'),
        icon: <LuLink size={32} />,
      },
    ],
  },
]

export const getDefaultEditorBlocks = (t: TFunction): EditorBlock[] => [
  {
    id: 'default-paragraph',
    kind: 'paragraph',
    label: t('blog:blockParagraph'),
    content: '',
  },
]

export const defaultEditorBlocks = getDefaultEditorBlocks(i18n.t.bind(i18n))

const quoteDefaultContent = stringifyTextBlockContent({
  text: '<em><br></em>',
  link: '',
})

export const createEditorBlock = (
  kind: EditorBlockKind,
  label: string,
): EditorBlock => ({
  id: `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  kind,
  label,
  content: kind === 'quote' ? quoteDefaultContent : '',
})
